/**
 * Admin Layout Component
 *
 * Layout wrapper for admin pages with sidebar and header.
 *
 * @fileoverview Admin layout with navigation
 */

import React, { useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import { Box, useColorModeValue } from '@chakra-ui/react';
import { AdminSidebar } from '../../components/admin/AdminSidebar';
import { AdminHeader } from '../../components/admin/AdminHeader';
import { useSidebarCollapse, isEditableTarget } from '../../hooks/useSidebarCollapse';

/**
 * Admin Layout Component
 *
 * Provides consistent layout for all admin pages:
 * - Fixed sidebar on the left (collapsible, persisted — shares the same
 *   preference key as the main app's sidebar via `useSidebarCollapse`)
 * - Fixed header at the top
 * - Scrollable main content area
 */
export const AdminLayout: React.FC = () => {
  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const { isCollapsed, toggle } = useSidebarCollapse();

  // Same Ctrl/Cmd+B shortcut as the main app's AppShell. Admin doesn't use
  // AppShell (it has its own fixed sidebar/header), so it needs its own
  // listener rather than inheriting one.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key.toLowerCase() !== 'b') return;
      if (!(event.ctrlKey || event.metaKey)) return;
      if (isEditableTarget(event.target)) return;
      event.preventDefault();
      toggle();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [toggle]);

  return (
    <Box
      minH="100vh"
      bg={bgColor}
      sx={{ '--xacto-sidebar-w': isCollapsed ? '64px' : '240px' }}
    >
      {/* Sidebar */}
      <AdminSidebar isCollapsed={isCollapsed} onToggleCollapse={toggle} />

      {/* Header */}
      <AdminHeader />

      {/* Main content */}
      <Box
        as="main"
        ml="var(--xacto-sidebar-w)"
        pt="16"
        minH="100vh"
        transition="margin-left 0.2s ease-in-out"
      >
        <Box p={6}>
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
};

export default AdminLayout;
